import { useEffect, useState } from 'react';
import { FaCheckCircle, FaTimes, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import './BookingActionConfirmModal.css';

const ANIMATION_MS = 180;

const VARIANT_CONFIG = {
  approve: {
    icon: FaCheckCircle,
    confirmClass: 'booking-confirm-btn--approve',
    iconClass: 'booking-confirm-icon--approve',
  },
  decline: {
    icon: FaTimesCircle,
    confirmClass: 'booking-confirm-btn--decline',
    iconClass: 'booking-confirm-icon--decline',
  },
  warning: {
    icon: FaExclamationTriangle,
    confirmClass: 'booking-confirm-btn--warning',
    iconClass: 'booking-confirm-icon--warning',
  },
};

const BookingActionConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  variant = 'approve',
  title,
  message = '',
  details = [],
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.approve;
  const Icon = config.icon;

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => setIsVisible(true));
      return undefined;
    }

    setIsVisible(false);
    const timeout = setTimeout(() => setShouldRender(false), ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const handleEsc = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [shouldRender, onClose, loading]);

  if (!shouldRender) return null;

  return (
    <div
      className={`booking-confirm-overlay ${isVisible ? 'open' : 'closing'}`}
      onClick={loading ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-confirm-title"
    >
      <div
        className={`booking-confirm-modal ${isVisible ? 'open' : 'closing'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`booking-confirm-icon ${config.iconClass}`}>
          <Icon />
        </div>
        <h2 id="booking-confirm-title">{title}</h2>
        {message ? <p className="booking-confirm-message">{message}</p> : null}

        {details.length > 0 && (
          <div className="booking-confirm-details">
            {details.map((item) => (
              <div key={item.label} className="booking-confirm-detail-row">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        )}

        <div className="booking-confirm-actions">
          <button
            type="button"
            className="booking-confirm-btn booking-confirm-btn--cancel"
            onClick={onClose}
            disabled={loading}
          >
            <FaTimes />
            <span>{cancelText}</span>
          </button>
          <button
            type="button"
            className={`booking-confirm-btn ${config.confirmClass}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? <span className="booking-confirm-spinner" /> : <Icon />}
            <span>{loading ? 'Processing...' : confirmText}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingActionConfirmModal;
